import React, { useId, useState } from 'react';
import './Card.scss';

interface CardProps {
  title: string;
  columnSize?: string; // Optional prop for column size, e.g., "col-md-5"
  children?: React.ReactNode; // Using children for body content
  cardBodyClass?: string; // Optional class for card body
  hideCardBody?: boolean; // Optional prop to hide the card body
  onToggle?: () => void; // Function to toggle visibility of card body
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onDeleteCard?: () => void;
  enableExpand?: boolean; // New optional prop for expand functionality
}

const Card: React.FC<CardProps> = ({
  title,
  children,
  columnSize = "col-md-12",
  hideCardBody = false,
  cardBodyClass = "",
  onMoveUp,
  onMoveDown,
  onDeleteCard,
  onToggle,
  enableExpand = false, // Default: expand feature disabled
}) => {
  const uniqueId = useId();
  const [isExpanded, setIsExpanded] = useState(false); // Tracks modal visibility

  const handleHeaderClick = (e: React.MouseEvent) => {
    // Prevent toggle if a button inside the header was clicked
    if (onToggle && !(e.target as HTMLElement).closest('button')) {
      onToggle();
    }
  };

  return (
    <div className={`${columnSize} m-0 py-2`}>
      <div className="card h-100">
        <div
          className="card-header"
          onClick={handleHeaderClick}
          style={{ cursor: onToggle ? 'pointer' : 'default' }}
        >
          <span>{title}</span>
          <div className="card-header-buttons">
            {onMoveUp && (
              <button
                onClick={onMoveUp}
                className="btn btn-outline-secondary card-header-button"
                title="Move Up"
              >
                <i className="bi bi-arrow-up"></i>
              </button>
            )}
            {onMoveDown && (
              <button
                onClick={onMoveDown}
                className="btn btn-outline-secondary card-header-button"
                title="Move Down"
              >
                <i className="bi bi-arrow-down"></i>
              </button>
            )}
            {onDeleteCard && (
              <button
                onClick={onDeleteCard}
                className="btn btn-outline-secondary card-header-button"
                title="Delete"
              >
                <i className="bi bi-trash"></i>
              </button>
            )}
            {enableExpand && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent header toggle
                  setIsExpanded(true); // Open expanded view
                }}
                className="btn btn-outline-secondary card-header-button"
                title="Expand"
              >
                <i className="bi bi-arrows-fullscreen"></i>
              </button>
            )}
            {onToggle && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent the header click event
                  onToggle();
                }}
                className="btn btn-outline-secondary card-header-button"
              >
                {hideCardBody ? (
                  <i className="bi bi-eye"></i> // Indicates that the body is hidden
                ) : (
                  <i className="bi bi-eye-slash"></i> // Indicates that the body is visible
                )}
              </button>
            )}
          </div>
        </div>
        {!hideCardBody && (
          <div className={`${cardBodyClass} card-body`}>{children}</div>
        )}
      </div>

      {/* Expanded view as a modal */}
      {isExpanded && (
        <div className="modal-overlay">
          <div className="modal-content">
          <div className="modal-header">
              <span>{title}</span>
              <button
                  className="btn-close"
                  onClick={() => setIsExpanded(false)} // Close expanded view
              >
                  <i className="bi bi-x-circle"></i> {/* Bruker Bootstrap-ikon */}
              </button>
          </div>
            <div className="modal-body">
              {/* Render the same content in the expanded view */}
              <div className={`${cardBodyClass} card-body`}>{children}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Card;